import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import Shipyard from "assets/images/Shipyard-bg.jpg";
import ShipLogo from "assets/images/ShipzzerLogo.png";
import { login, sendResetPassword } from "actions/auth";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

toast.configure();

const Basic = ({ login, isAuthenticated, t }) => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const { email, password } = formData;

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isResetting, setIsResetting] = useState(false);

  const handleInputChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = async () => {
    // Trim inputs to avoid spaces-only entries
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    // Check if fields are empty
    if (!trimmedEmail || !trimmedPassword) {
      toast.error(t("Please fill in all fields."), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
      });
      return;
    }

    try {
      await login(trimmedEmail, trimmedPassword);
      setError(""); // Clear any previous error
    } catch (err) {
      console.error("Login failed:", err);
      toast.error(t("Incorrect email or password."), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
      });
    }
  };


  const handlePasswordReset = async () => {
    if (isResetting) return;

    const trimmedEmail = email.toLowerCase().trim();
    if (!trimmedEmail || !/\S+@\S+\.\S+/.test(trimmedEmail)) {
      toast.error(t("Please enter a valid email."), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
      });
      return;
    }

    setIsResetting(true);

    try {
      await sendResetPassword("Customer", trimmedEmail, "vbs");
      toast.success(t("Password reset link sent! Check your inbox."), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 4000,
      });
    } catch (error) {
      console.error("Reset error:", error);
      const message = error.message || t("Error during password reset.");
      toast.error(`${message}`, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 4000,
      });
    } finally {
      setIsResetting(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <BasicLayout image={Shipyard}>
      <Card
        sx={{
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          paddingBottom: "20px",
        }}
      >
        <MDBox display="flex" justifyContent="center" mt={3}>
          <img src={ShipLogo} style={{ width: "140px" }} alt={t("Shipzzer Logo")} />
        </MDBox>

        <MDBox pt={3} pb={3} px={4}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                type="email"
                label={t("Email")}
                value={email}
                name="email"
                onChange={handleInputChange}
                fullWidth
              />
            </MDBox>

            <MDBox mb={1}>
              <MDInput
                type={showPassword ? "text" : "password"}
                label={t("Password")}
                value={password}
                name="password"
                onChange={handleInputChange}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>

            {error && (
              <MDBox
                mb={2}
                textAlign="center"
                sx={{ color: "red", fontSize: "0.9rem" }}
              >
                {error}
              </MDBox>
            )}

            <MDBox mb={2} textAlign="right">
              <Link
                to="#"
                onClick={handlePasswordReset}
                style={{
                  fontSize: "0.85rem",
                  textDecoration: "none",
                  color: "#1976d2",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                {t("Forgot Password?")}
              </Link>
            </MDBox>

            <MDBox mt={3} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleSubmit}
                fullWidth
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: "600",
                  py: 1.2,
                }}
              >
                {t("Sign In")}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
});

export default connect(mapStateToProps, { login })(withTranslation()(Basic));
